/**
 * CallbackStatusBadge — color-coded pill for `scheduled_callbacks.execution_status`
 * (Story 7.13 AC3, using the palette from docs/epics/epic-E4A-callback-e2e-audit.md).
 *
 *   pending   → slate     (not yet due)
 *   executing → blue      (worker claimed this tick; animated pulse)
 *   executed  → green     (sent successfully)
 *   failed    → red       (all retries exhausted)
 *   cancelled → gray      (admin cancelled or worker gave up with reason)
 *   expired   → amber     (missed the 24h Meta window + no template available)
 */

export type CallbackExecutionStatus =
  | 'pending'
  | 'executing'
  | 'executed'
  | 'failed'
  | 'cancelled'
  | 'expired';

const PALETTE: Record<CallbackExecutionStatus, string> = {
  pending: 'bg-slate-500/20 text-slate-700 dark:text-slate-300',
  executing: 'bg-blue-500/20 text-blue-700 dark:text-blue-300 animate-pulse',
  executed: 'bg-green-500/20 text-green-700 dark:text-green-300',
  failed: 'bg-red-500/20 text-red-700 dark:text-red-300',
  cancelled: 'bg-muted text-muted-foreground',
  expired: 'bg-amber-500/20 text-amber-700 dark:text-amber-300',
};

export function CallbackStatusBadge({ status }: { status: CallbackExecutionStatus }) {
  const palette = PALETTE[status] ?? 'bg-muted text-muted-foreground';
  return (
    <span
      data-testid={`callback-status-badge-${status}`}
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${palette}`}
    >
      {status}
    </span>
  );
}
